import React from 'react'

// Two-icon connectivity indicator for a module (PCB): WiFi + BLE mesh.
//
// Connectivity model (per Dayro hardware):
//   - Only the Master (WIFI_BLE_DUAL_PROXY, role 2 or 9) has a WiFi radio.
//     Its WiFi status comes from the MQTT LWT topic (online/offline).
//   - Every other module is a BLE-mesh-only node — no WiFi. Its WiFi icon
//     is N/A (faded).
//   - Mesh reachability is gated by the Master: if the Master is offline,
//     the whole mesh is unreachable, so every node shows Mesh offline
//     regardless of its last-known state. If the Master is online, a node's
//     mesh status is its last-known isOnline (the backend refreshes this via
//     periodic Device_Status; no response → treated as offline/unknown).

const GREEN = '#16A34A'
const RED = '#B91C1C'
const GREY = '#9CA3AF'
const FADED = '#D1D5DB'

export const isMasterPcb = (pcb: any) => pcb?.role === 2 || pcb?.role === 9

interface Props {
    pcb: any
    masterOnline: boolean
    // Derived: is ANY element (switch) of this PCB currently online? The
    // backend keeps switch.switchIsOnline fresh but does NOT propagate it to
    // pcb.isOnline, so for mesh nodes we infer reachability from the elements.
    moduleOnline?: boolean
}

const ConnectivityIcons = ({ pcb, masterOnline, moduleOnline = false }: Props) => {
    const master = isMasterPcb(pcb)

    // WiFi — only meaningful for the Master.
    let wifiColor = FADED
    let wifiTitle = 'WiFi: N/A — mesh node'
    if (master) {
        if (pcb.isOnline === true) { wifiColor = GREEN; wifiTitle = 'WiFi: Online' }
        else { wifiColor = RED; wifiTitle = 'WiFi: Offline' }
    }

    // Mesh — gated by the Master being online.
    let meshColor = GREY
    let meshTitle = 'Mesh: Offline / Unknown'
    if (!masterOnline) {
        meshColor = GREY
        meshTitle = 'Mesh: Offline (master down)'
    } else if (master) {
        meshColor = GREEN
        meshTitle = 'Mesh: Online (mesh root)'
    } else if (moduleOnline) {
        meshColor = GREEN
        meshTitle = 'Mesh: Online'
    }

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <i
                className="bx bx-wifi"
                title={wifiTitle}
                aria-label={wifiTitle}
                style={{ fontSize: 18, color: wifiColor }}
            />
            <i
                className="bx bx-bluetooth"
                title={meshTitle}
                aria-label={meshTitle}
                style={{ fontSize: 18, color: meshColor }}
            />
        </span>
    )
}

export default ConnectivityIcons
